import { createContext, useCallback, useContext, useEffect, useMemo } from "react";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  ListItemText,
  Link as MaterialLink,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import {
  bindFocus,
  bindHover,
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { Link, useLocation } from "react-router-dom";

import type { IAeronetNav, SubMenuItem } from "./types";
import { openSubMenus, useNavGates } from "./navGates";
import { bySetOrder } from "./navSort";
import {
  dropdownMenuListItemTextSx,
  dropdownMenuListSx,
  subMenuPaperSx,
  navDropdownDividerSx,
  navDropdownHeaderSx,
  navDropdownHomeIconSx,
  navDropdownMenuItemSx,
  navBarDropdownMenuPaperSx,
} from "../components/dropdownMenuStyles";

const CascadingContext = createContext({
  parentPopupState: null,
  rootPopupState: null,
});

function CascadingMenuItem({ onClick, sx, ...props }: {
  onClick?: (event: React.MouseEvent) => void;
  sx?: React.ComponentProps<typeof MenuItem>["sx"];
  [key: string]: any;
}) {
  const { rootPopupState } = useContext(CascadingContext);
  if (!rootPopupState) throw new Error("must be used inside a CascadingMenu");
  const handleClick = useCallback(
    (event) => {
      // @ts-ignore
      rootPopupState.close(event);
      if (onClick) onClick(event);
    },
    [rootPopupState, onClick]
  );

  return (
    <MenuItem
      sx={[navDropdownMenuItemSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...props}
      onContextMenu={(event) => {
        event.stopPropagation();
      }}
      onClick={handleClick}
    />
  );
}

function CascadingSubmenu({ popupId, title, to, ...props }) {
  const { parentPopupState } = useContext(CascadingContext);
  const popupState = usePopupState({
    popupId,
    variant: "popover",
    parentPopupState,
  });

  return (
    <>
      <MenuItem
        component={Link}
        to={to}
        sx={navDropdownMenuItemSx}
        {...bindHover(popupState)}
        {...bindFocus(popupState)}
        // @ts-ignore
        onClick={parentPopupState.close}
      >
        <Typography variant="inherit" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <ChevronRightIcon
          fontSize="small"
          sx={{
            ml: 1,
            color: "inherit",
            opacity: 0.55,
          }}
        />
      </MenuItem>
      <CascadingMenu
        {...props}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        popupState={popupState}
      />
    </>
  );
}

function CascadingMenu({ popupState, flushToNav = false, ...props }) {
  const { rootPopupState } = useContext(CascadingContext);
  // A non-null rootPopupState in the INHERITED context means this CascadingMenu
  // is being rendered from CascadingSubmenu, i.e. it IS a submenu — the
  // top-level call (from MenuPopupState) sees the default `null` and becomes
  // the root itself, below.
  const isSubmenu = rootPopupState != null;
  const context = useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState]
  );

  // Root nav dropdown: plain MUI Menu (bindMenu + bindTrigger) — opens on
  // click and needs its Modal backdrop to close on an outside click.
  //
  // Submenu level: HoverMenu, per material-ui-popup-state's own README
  // ("Using Popover and Menu with bindHover"). A plain Menu's bindMenu stamps
  // `id: popupId` on the MUI Modal root, which covers the viewport;
  // `isElementInPopup` resolves that id with `getElementById` and treats the
  // overlay as "inside the popup", so `bindHover`'s mouse-leave close never
  // fires and the trigger row's own click-to-navigate gets swallowed by the
  // backdrop. HoverMenu sets `pointerEvents: none` on its root (with
  // `pointerEvents: auto` on the Paper), so the overlay no longer intercepts
  // pointer events — the submenu opens/closes on hover as bindHover intends,
  // and the parent row underneath stays clickable.
  const MenuComponent = isSubmenu ? HoverMenu : Menu;

  return (
    <CascadingContext.Provider value={context}>
      <MenuComponent
        {...props}
        {...bindMenu(popupState)}
        marginThreshold={8}
        // The default Grow transition scales the paper up over ~250ms. A click
        // inside that window lands on a row that has since moved, or on the
        // backdrop, and reads to the user as "the menu just didn't work".
        transitionDuration={0}
        PaperProps={{
          sx: flushToNav ? navBarDropdownMenuPaperSx : subMenuPaperSx,
          elevation: 0,
        }}
        MenuListProps={{
          dense: true,
          disablePadding: true,
          // Don't auto-focus the house/title row (avoids grey focus bg on open)
          autoFocusItem: false,
          sx: dropdownMenuListSx,
        }}
      />
    </CascadingContext.Provider>
  );
}

/**
 * A sub-menu's entries as this user may see them (PATTERNS.md BP-90, H131).
 * Mounted only for a sub-menu that HAS a gated entry, so every other section
 * renders exactly as before and never pays for the permission check. A gate
 * that is still loading reads closed: the entry appears once known, and never
 * flashes at a user it would keep out.
 */
function GatedSubMenuItems({
  items,
  render,
}: {
  items: ReadonlyArray<SubMenuItem>;
  render: (visible: ReadonlyArray<SubMenuItem>) => React.ReactNode;
}) {
  const { open } = useNavGates();
  return <>{render(openSubMenus(items, open))}</>;
}

/** Composed route for a submenu child (was SubMenuLink's internal `to`). */
const subMenuTo = (menuTo = "", childTo: string) =>
  childTo ? `${menuTo}/${childTo}` : menuTo;

// `setActiveNavItem` / `activeNavItem` come from Nav.tsx, which owns the
// persisted active-section state. Optional with a no-op default because this
// component is also mounted WITHOUT them — src/stories/Nav/Menu.stories.tsx and
// src/app/__tests__/Menu.test.tsx render it standalone. When they were required
// in name only (typed but not defaulted) those mounts crashed in the
// open-the-menu effect below with "setActiveNavItem is not a function".
export interface MenuPopupStateProps {
  navItem: IAeronetNav;
  setActiveNavItem?: (id: string | null) => void;
  activeNavItem?: string | null;
}

const NOOP = () => {};

const MenuPopupState: React.FC<MenuPopupStateProps> = (props) => {
  const { navItem, setActiveNavItem = NOOP, activeNavItem } = props;
  const popupState = usePopupState({ variant: "popover", popupId: navItem.id });
  const location = useLocation();
  const theme = useTheme();

  // Opening the menu marks this section active (same as selecting a child)
  useEffect(() => {
    if (popupState.isOpen) {
      setActiveNavItem(navItem.id);
    }
  }, [popupState.isOpen, navItem.id, setActiveNavItem]);

  const SECTION_LANDING_ROUTES: Record<string, string> = {
    "Directory": "directory",
    "Sales": "sales",
    "Purchasing": "purchasing",
    "Tech Records": "tech-records",
    "Maintenance": "maintenance",
    "Stores": "stores",
    "Finance": "finance",
    "Safety & Quality": "safety-quality",
    "System Admin": "system-admin",
    // Without these two rows the header falls through to the generic
    // `landing/${encodeURIComponent(name)}` stub below — i.e. the
    // /app/landing/Pilots and /app/landing/Flight%20Admin shapes. The built
    // homes are /app/pilots and /app/flight-admin.
    "Pilots": "pilots",
    "Flight Admin": "flight-admin",
    "Operations": "operations",
    "Mobile": "mobile-emulator",
  };

  // Every dropdown home item lands somewhere: a built section dashboard when
  // one exists, else the generic landing stub for that section.
  const headerLandingTo =
    SECTION_LANDING_ROUTES[navItem.name] ||
    `landing/${encodeURIComponent(navItem.name)}`;

  return (
    <>
      <MaterialLink
        sx={{
          textDecoration: "none",
          "&:hover": {
            color: theme.palette.primary.main,
          },
          "&:focus": {
            color: theme.palette.primary.main,
          },
          ...(activeNavItem === navItem.id
            ? {
                color: theme.palette.primary.main,
              }
            : {
                color: popupState.isOpen
                  ? theme.palette.primary.main
                  : grey[400],
              }),
          fontFamily:
            activeNavItem === navItem.id || popupState.isOpen
              ? '"Roboto-Bold", "Roboto", sans-serif'
              : '"Roboto-Medium", "Roboto", sans-serif',
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1,
          display: "inline-block",
          cursor: "pointer",
        }}
        underline="none"
        {...bindTrigger(popupState)}
        {...bindFocus(popupState)}
      >
        {navItem.name}
      </MaterialLink>
      <CascadingMenu
        popupState={popupState}
        flushToNav
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <CascadingMenuItem
          component={Link}
          to={headerLandingTo}
          onClick={() => {
            setActiveNavItem(navItem.id);
            popupState.close();
          }}
          sx={[
            navDropdownHeaderSx,
            // navDropdownHeaderSx sets `pointerEvents: none` — correct when this
            // row was a non-interactive label, but the row IS the link to the
            // section landing page now, so it has to accept clicks. Re-enable
            // here rather than in the shared style: BreadcrumbDropdownMenu and
            // HelpMenu reuse it for rows that really are labels.
            {
              pointerEvents: "auto",
              cursor: "pointer",
              "&:hover, &:focus, &.Mui-focusVisible, &.Mui-selected": {
                backgroundColor: "rgba(1, 138, 144, 0.08) !important",
              },
            },
          ]}
        >
          {/* Icon and label are DIRECT children: navDropdownHeaderSx supplies
              `gap: 1` between them, and a wrapper element here would absorb it —
              the flex row's only child would be the wrapper, leaving the icon
              flush against the word. Matches prod (Menu.tsx ~:266) and this
              style's other two consumers, BreadcrumbDropdownMenu and HelpMenu. */}
          <HomeOutlinedIcon sx={navDropdownHomeIconSx} />
          <Typography variant="inherit">{navItem.name}</Typography>
        </CascadingMenuItem>
        <Box component="li" aria-hidden sx={navDropdownDividerSx} />
        {bySetOrder(navItem.children).map((menuItem, index) =>
          menuItem.subMenus && menuItem.subMenus.length > 0 ? (
            <CascadingSubmenu
              popupId="moreChoicesCascadingMenu"
              key={index}
              title={menuItem.name}
              to={menuItem.to}
            >
              {(() => {
                const renderEntries = (entries: ReadonlyArray<SubMenuItem>) =>
                  bySetOrder(entries).map((subMenu) => (
                    <CascadingMenuItem
                      key={subMenu.to}
                      component={Link}
                      to={subMenuTo(menuItem.to, subMenu.to)}
                      value={subMenu.to}
                      onClick={() => {
                        setActiveNavItem(navItem.id);
                        popupState.close();
                      }}
                    >
                      <ListItemText sx={dropdownMenuListItemTextSx}>
                        <Typography variant="inherit">{subMenu.name}</Typography>
                      </ListItemText>
                    </CascadingMenuItem>
                  ));
                const subMenus = menuItem.subMenus ?? [];
                return subMenus.some((entry) => entry.gate) ? (
                  <GatedSubMenuItems items={subMenus} render={renderEntries} />
                ) : (
                  renderEntries(subMenus)
                );
              })()}
            </CascadingSubmenu>
          ) : (
            <CascadingMenuItem
              key={menuItem.id}
              value={menuItem.id}
              {...(menuItem.to
                ? { component: Link, to: menuItem.to }
                : { component: "a", href: menuItem.url })}
              onClick={() => {
                setActiveNavItem(navItem.id);
                popupState.close();
              }}
              selected={
                menuItem.to
                  ? location.pathname === "/app/" + menuItem.to
                  : false
              }
            >
              <ListItemText sx={dropdownMenuListItemTextSx}>
                <Typography variant="inherit">{menuItem.name}</Typography>
              </ListItemText>
            </CascadingMenuItem>
          )
        )}
      </CascadingMenu>
    </>
  );
};

export default MenuPopupState;
